import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Popover,
  Stack,
  Typography
} from "@mui/material";
import { DotsThreeVerticalIcon, TrashIcon } from "@phosphor-icons/react";
import { LoadingScreen } from "components/LoadingScreen";
import {
  Banner,
  DeleteModal,
  Notification,
  PhoneInputField,
  Select,
  TextField
} from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { GOOGLE_API_KEY } from "config";
import {
  ADDRESSES,
  useCities,
  useCountries,
  useRegions,
  useSubcities,
  useWoredas,
  useZones
} from "features/members";
import i18next from "i18next";
import { useEffect, useState } from "react";
import { useAuthUser } from "react-auth-kit";
import { usePlacesWidget } from "react-google-autocomplete";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCentralStore } from "stores";
import {
  useDeleteDenomination,
  useDenomination,
  useUpdateDenomination
} from "../api";
import { updateDenominationSchema } from "../validation";

export function DenominationProfile() {
  const [anchorElement, setAnchorElement] = useState(null);

  const setHeaderTitle = useCentralStore(state => state.setHeaderTitle);
  const setHeaderBackground = useCentralStore(
    state => state.setHeaderBackground
  );
  const { id: denominationId } = useParams();
  const denominationMutation = useUpdateDenomination();
  const denominationDeleteMutation = useDeleteDenomination();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const openMenu = Boolean(anchorElement);

  const denominationQuery = useDenomination({
    denominationId,
    config: {
      enabled: !!denominationId
    }
  });

  const auth = useAuthUser();
  const userId = auth()?.id;
  const denominationCreatorId =
    denominationQuery.data?.result?.created_by?.id ||
    denominationQuery.data?.result?.created_by;
  const isCreatedByUser = String(userId) === String(denominationCreatorId);

  const {
    register,
    handleSubmit,
    formState: { errors, defaultValues },
    reset,
    watch,
    setValue,
    control
  } = useForm({
    defaultValues: {
      name: denominationQuery.data?.result?.name || "",
      acronyms: denominationQuery.data?.result?.acronyms || "",
      email: denominationQuery.data?.result?.email || "",
      phone: denominationQuery.data?.result?.phone || "",
      p_o_box: denominationQuery.data?.result?.p_o_box || "",
      country_id: denominationQuery.data?.result?.country_id || "",
      region_id: denominationQuery.data?.result?.region_id || "",
      zone_id: denominationQuery.data?.result?.zone_id || "",
      city_id: denominationQuery.data?.result?.city_id || "",
      sub_city_id: denominationQuery.data?.result?.sub_city_id || "",
      woreda_id: denominationQuery.data?.result?.woreda_id || "",
      kebele: denominationQuery.data?.result?.kebele || "",
      house_number: denominationQuery.data?.result?.house_number || "",
      specific_location:
        denominationQuery.data?.result?.specific_location || "",
      description: denominationQuery.data?.result?.description || "",
      denomination_structure_type_id:
        denominationQuery.data?.result?.denomination_structure_type_id || "",
      parent_id: denominationQuery.data?.result?.parent_id || ""
    },
    resolver: yupResolver(updateDenominationSchema)
  });

  useEffect(() => {
    if (denominationQuery.data?.result) {
      reset({
        name: denominationQuery.data.result.name || "",
        acronyms: denominationQuery.data.result.acronyms || "",
        email: denominationQuery.data.result.email || "",
        phone: denominationQuery.data.result.phone || "",
        p_o_box: denominationQuery.data.result.p_o_box || "",
        country_id: denominationQuery.data.result.country_id || "",
        region_id: denominationQuery.data.result.region_id || "",
        zone_id: denominationQuery.data.result.zone_id || "",
        city_id: denominationQuery.data.result.city_id || "",
        sub_city_id: denominationQuery.data.result.sub_city_id || "",
        woreda_id: denominationQuery.data.result.woreda_id || "",
        kebele: denominationQuery.data.result.kebele || "",
        house_number: denominationQuery.data.result.house_number || "",
        specific_location:
          denominationQuery.data.result.specific_location || "",
        description: denominationQuery.data.result.description || "",
        denomination_structure_type_id:
          denominationQuery.data.result.denomination_structure_type_id || "",
        parent_id: denominationQuery.data.result.parent_id || ""
      });
    }
  }, [denominationQuery.data]);

  useEffect(() => {
    setHeaderTitle(t("denominations.createDenomination.denominationProfile"));

    return () => setHeaderTitle("");
  }, [i18next.language]);

  useEffect(() => {
    setHeaderBackground(false);

    return () => setHeaderBackground(true);
  }, []);

  const countriesQuery = useCountries();

  useEffect(() => {
    if (countriesQuery.data && denominationQuery.data?.result) {
      setValue(
        "country_id",
        String(denominationQuery.data.result.country_id || "")
      );
    }
  }, [countriesQuery.data, denominationQuery.data]);

  const regionsQuery = useRegions({
    countryId: watch("country_id"),
    config: {
      enabled: !!watch("country_id")
    }
  });

  useEffect(() => {
    if (regionsQuery.data && denominationQuery.data?.result) {
      setValue(
        "region_id",
        String(denominationQuery.data.result.region_id || "")
      );
    }
  }, [regionsQuery.data, denominationQuery.data]);

  const zonesQuery = useZones({
    regionId: watch("region_id"),
    config: {
      enabled: !!watch("region_id")
    }
  });

  useEffect(() => {
    if (zonesQuery.data && denominationQuery.data?.result) {
      setValue("zone_id", String(denominationQuery.data.result.zone_id || ""));
    }
  }, [zonesQuery.data, denominationQuery.data]);

  const citiesQuery = useCities({
    zoneId: watch("zone_id"),
    config: {
      enabled: !!watch("zone_id")
    }
  });

  useEffect(() => {
    if (citiesQuery.data && denominationQuery.data?.result) {
      setValue("city_id", String(denominationQuery.data.result.city_id || ""));
    }
  }, [citiesQuery.data, denominationQuery.data]);

  const subcitiesQuery = useSubcities({
    cityId: watch("city_id"),
    config: {
      enabled: !!watch("city_id")
    }
  });

  useEffect(() => {
    if (subcitiesQuery.data && denominationQuery.data?.result) {
      setValue(
        "sub_city_id",
        String(denominationQuery.data.result.sub_city_id || "")
      );
    }
  }, [subcitiesQuery.data, denominationQuery.data]);

  const woredasQuery = useWoredas({
    subcityId: watch("sub_city_id"),
    config: {
      enabled: !!watch("sub_city_id")
    }
  });

  useEffect(() => {
    if (woredasQuery.data && denominationQuery.data?.result) {
      setValue(
        "woreda_id",
        String(denominationQuery.data.result.woreda_id || "")
      );
    }
  }, [woredasQuery.data, denominationQuery.data]);

  const { ref: placesRef } = usePlacesWidget({
    apiKey: GOOGLE_API_KEY,
    onPlaceSelected: place => {
      setValue("latitude", place.geometry.location.lat());
      setValue("longitude", place.geometry.location.lng());
    },
    options: {
      types: ["establishment"],
      componentRestrictions: { country: "et" }
    }
  });

  const onSubmit = values => {
    denominationMutation.mutate({
      denominationData: values,
      denominationId
    });
  };

  const handleClick = event => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  if (denominationQuery.isLoading || countriesQuery.isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Box>
      <Notification />
      <DeleteModal
        name={denominationQuery.data?.result?.name}
        onDelete={() => {
          denominationDeleteMutation.mutate({
            denominationId
          });
        }}
      />
      <Banner>
        <IconButton
          onClick={handleClick}
          sx={{
            position: "absolute",
            top: "16px",
            right: "16px"
          }}
        >
          <DotsThreeVerticalIcon color="white" />
        </IconButton>
      </Banner>
      <Stack
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        spacing={4}
        sx={{ mt: 4 }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 2
          }}
        >
          <TextField
            label={t("denominations.createDenomination.denominationName")}
            name="name"
            error={!!errors.name?.message}
            helperText={errors.name?.message}
            register={register}
            isLabelBold={true}
            disabled={!isCreatedByUser}
          />

          <TextField
            label={t("denominations.createDenomination.denominationAcronym")}
            name="acronyms"
            error={!!errors.acronyms?.message}
            helperText={errors.acronyms?.message}
            register={register}
            isLabelBold={true}
            disabled={!isCreatedByUser}
          />

          <TextField
            label={t("denominations.createDenomination.denominationPOBox")}
            name="p_o_box"
            error={!!errors.p_o_box?.message}
            helperText={errors.p_o_box?.message}
            register={register}
            isLabelBold={true}
            disabled={!isCreatedByUser}
          />

          <TextField
            label={t("form.common.email")}
            name="email"
            error={!!errors.email?.message}
            helperText={errors.email?.message}
            register={register}
            isLabelBold={true}
            disabled={!isCreatedByUser}
          />

          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <PhoneInputField
                variant="filled"
                label={t("form.common.phone")}
                error={!!errors.phone?.message}
                helperText={errors.phone?.message}
                disabled={!isCreatedByUser}
                isLabelBold={1}
                {...field}
              />
            )}
          />

          {denominationQuery.data?.result?.denomination_structure_type && (
            <TextField
              label="Structure Type"
              name="denomination_structure_type_id"
              defaultValue={
                denominationQuery.data?.result?.denomination_structure_type
                  ?.name || ""
              }
              isLabelBold={true}
              disabled
            />
          )}

          {denominationQuery.data?.result?.parent && (
            <TextField
              label="Parent Structure"
              name="parent_id"
              defaultValue={denominationQuery.data?.result?.parent?.name || ""}
              isLabelBold={true}
              disabled
            />
          )}
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t("denominations.createDenomination.denominationHQAddress")}
          </Typography>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={3}>
              <Select
                isLabelBold={1}
                label={t("form.common.country")}
                name={`country_id`}
                options={
                  countriesQuery.data?.result?.map(country => ({
                    optionLabel: country?.name,
                    value: country?.id
                  })) || []
                }
                register={register}
                error={!!errors.country_id?.message}
                helperText={errors.country_id?.message}
                disabled={!isCreatedByUser}
              />
            </Grid>
            <Grid item xs={3}>
              <Select
                isLabelBold={1}
                label={t("form.common.region")}
                name={`region_id`}
                options={
                  regionsQuery.data?.result?.map(region => ({
                    optionLabel: region?.name,
                    value: region?.id
                  })) || []
                }
                register={register}
                error={!!errors.region_id?.message}
                helperText={errors.region_id?.message}
                onChange={e => {
                  setValue("region_id", e.target.value, {
                    shouldValidate: true
                  });

                  if (e.target.value === ADDRESSES.ADDIS_ABABA.REGION_ID) {
                    setValue("zone_id", ADDRESSES.ADDIS_ABABA.ZONE_ID, {
                      shouldValidate: true
                    });
                    setValue("city_id", ADDRESSES.ADDIS_ABABA.CITY_ID, {
                      shouldValidate: true
                    });
                  } else {
                    setValue("zone_id", "", {
                      shouldValidate: true
                    });
                    setValue("city_id", "", {
                      shouldValidate: true
                    });
                  }
                }}
                disabled={!isCreatedByUser}
              />
            </Grid>
            {watch("region_id") !== ADDRESSES.ADDIS_ABABA.REGION_ID && (
              <>
                <Grid item xs={3}>
                  <Select
                    isLabelBold={1}
                    label={t("form.common.zone")}
                    name={`zone_id`}
                    options={
                      zonesQuery.data?.result?.map(zone => ({
                        optionLabel: zone?.name,
                        value: zone?.id
                      })) || []
                    }
                    register={register}
                    error={!!errors.zone_id?.message}
                    helperText={errors.zone_id?.message}
                    disabled={
                      watch("region_id") === ADDRESSES.ADDIS_ABABA.REGION_ID ||
                      !isCreatedByUser
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <Select
                    isLabelBold={1}
                    label={t("form.common.city")}
                    name={`city_id`}
                    options={
                      citiesQuery.data?.result?.map(city => ({
                        optionLabel: city?.name,
                        value: city?.id
                      })) || []
                    }
                    register={register}
                    error={!!errors.city_id?.message}
                    helperText={errors.city_id?.message}
                    disabled={
                      watch("region_id") === ADDRESSES.ADDIS_ABABA.REGION_ID ||
                      !isCreatedByUser
                    }
                  />
                </Grid>
              </>
            )}
            <Grid item xs={3}>
              <Select
                isLabelBold={1}
                label={t("form.common.subcity")}
                name={`sub_city_id`}
                options={
                  subcitiesQuery.data?.result?.map(subcity => ({
                    optionLabel: subcity?.name,
                    value: subcity?.id
                  })) || []
                }
                register={register}
                error={!!errors.sub_city_id?.message}
                helperText={errors.sub_city_id?.message}
                disabled={!isCreatedByUser}
              />
            </Grid>
            <Grid item xs={3}>
              <Select
                isLabelBold={1}
                label={t("form.common.woreda")}
                name={`woreda_id`}
                options={
                  woredasQuery.data?.result?.map(woreda => ({
                    optionLabel: woreda?.name,
                    value: woreda?.id
                  })) || []
                }
                register={register}
                error={!!errors.woreda_id?.message}
                helperText={errors.woreda_id?.message}
                disabled={!isCreatedByUser}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label={t("churches.createChurch.kebele")}
                name="kebele"
                error={!!errors.kebele?.message}
                helperText={errors.kebele?.message}
                register={register}
                isLabelBold={true}
                disabled={!isCreatedByUser}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label={t("churches.createChurch.houseNumber")}
                name="house_number"
                error={!!errors.house_number?.message}
                helperText={errors.house_number?.message}
                register={register}
                isLabelBold={true}
                disabled={!isCreatedByUser}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label={t("churches.createChurch.specificLocation")}
                name="specific_location"
                error={!!errors.specific_location?.message}
                helperText={errors.specific_location?.message}
                register={register}
                isLabelBold={true}
                ref={placesRef}
                placeholder=""
                disabled={!isCreatedByUser}
              />
            </Grid>
          </Grid>
        </Box>
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t("churches.profile.Description")}
          </Typography>
          <Stack
            sx={{
              backgroundColor: "#DFF5F5",
              borderRadius: "5px",
              px: 4,
              py: 2
            }}
          >
            <TextField
              label={t("churches.profile.AboutDenomination")}
              name="description"
              error={!!errors.description?.message}
              helperText={errors.description?.message}
              register={register}
              isLabelBold={true}
              disabled={!isCreatedByUser}
            />
          </Stack>
        </Stack>

        <MutationError
          error={denominationMutation.error || denominationDeleteMutation.error}
        />

        {isCreatedByUser && (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              color="neutral"
              size="large"
              variant="contained"
              onClick={() => {
                reset({ ...defaultValues });
                navigate(-1);
              }}
              disabled={denominationMutation.isLoading}
            >
              {t("form.common.cancel")}
            </Button>
            <Button
              type="submit"
              size="large"
              variant="contained"
              disabled={denominationMutation.isLoading}
            >
              {t("form.common.submit")}
            </Button>
          </Stack>
        )}
      </Stack>

      <Popover
        anchorEl={anchorElement}
        id="denomination-menu"
        open={openMenu}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 2
        }}
      >
        <DenominationProfileMenu />
      </Popover>
    </Box>
  );
}

const DenominationProfileMenu = () => {
  const openDeleteModal = useCentralStore(state => state.openDeleteModal);

  const { t } = useTranslation();

  return (
    <Stack
      sx={{
        backgroundColor: "white",
        p: 1
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          py: 1,
          px: 2,
          cursor: "pointer",
          color: "#F44949",
          "&:hover": {
            backgroundColor: "#FCD1D1"
          }
        }}
        onClick={openDeleteModal}
      >
        <TrashIcon size={16} />
        <Typography variant="caption">{t("form.common.delete")}</Typography>
      </Stack>
    </Stack>
  );
};
